import { NextPage } from 'next';
import React from 'react';
import {
  Button, ButtonColor, ButtonSize, ButtonVariant, Grid, Spacing, SpacingName, SymbolDirection, Typography, TypographyName, ICON_ARROW_CIRCLE,
} from '@doveit/lego';
import Image from 'next/image';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { importDynamicData } from '../services/importDynamicData';
import * as domain from '../domain/graphcms';
import { AGENT_BY_SLUG } from '../client/graphql/queries/agent';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import PageSection from '../client/components/page-section/PageSection';
import CardsHList from '../client/components/cards-hlist/CardsHList';
import ReviewCard from '../client/components/review-card/ReviewCard';
import { CTA, TextAlign } from '../client/types';
import Markdown from '../client/components/markdown/Markdown';
import {
  buildAgenciesURLObject,
  buildAgencyByLocalityURLObject, buildAgentURLObject, buildEvaluationURLObject, buildListingURLObject, buildRealEstateAgencyURLObject, buildSerpURLObject, goToPage, JOIN_OUR_AGENTS_AS, JOIN_OUR_AGENTS_URL, relativeToAbsoluteURL, URLObject,
} from '../client/utils/links';
import { OFFICE_BUILDING_MARKER_OUTLINE } from '../client/themes/icons';
import * as styles from '../client/templates/agent/Agent.styles';
import { template } from '../client/utils/string/string';
import { getAll, PropertiesResponse } from '../services/properties/properties';
import { Status } from '../domain/status';
import { SortOrder } from '../domain/pagination';
import { getLogger } from '../logger/isomorphic';
import PropertySearchResultCardList from '../client/components/property-search-result-card-list/PropertySearchResultCardList';
import CreateLeadSection from '../client/templates/common/create-lead-section/CreateLeadSection';
import { sendLeadSubmitEventAndRedirect } from '../client/utils/tracking';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import { Lead } from '../services/dtos';
import { getAgentStructuredData } from '../data/structured/agent';
import { getStructuredData } from '../data/structured';
import SoldPropertyCard from '../client/components/sold-property-card/SoldPropertyCard';
import { requireFromStaticAsset } from '../client/utils/assetsUtils';
import useRefCallback from '../client/hooks/useRefCallback/useRefCallback';
import { goToExternalUrl } from '../client/utils/url/utils';
import { DOVEIT_TRUSTPILOT_REVIEWS_PAGE } from '../client/containers/list-reviews/ListReviews';

export const AGENT_PAGE_LEAD_SOURCE = 'personal';

const SOLD_PROPERTY_RESULT_SIZE = 3;
const PROPERTY_RESULT_SIZE = 3;

const AGENT_IMAGE_WIDTH = 252;
const AGENT_IMAGE_HEIGHT = 252;

const logger = getLogger();

const EMPTY_PROPERTY_RESPONSE = {
  content: [],
  totalElements: 0,
} as unknown as PropertiesResponse;

export interface AgentPageProps {
  SEO: PageHeadProps['SEO'],
  agent: domain.Agent,
  intro: {
    title: string,
    cta: CTA,
  },
  reviews: {
    title: string,
    trustPilot: CTA,
    cta: CTA,
  },
  soldProperties: {
    title: string,
    properties: Pick<PropertiesResponse, 'content' | 'totalElements'>,
  },
  latestProperties: {
    title: string,
    properties: Pick<PropertiesResponse, 'content' | 'totalElements'>,
    cta: CTA,
  },
  sellPropertyBox: {
    title: string,
    description: string,
    cta: CTA,
  },
  workWithAgentBox: {
    title: string,
    description: string,
    cta: CTA,
  },
  form: {
    title: string,
    label: string,
  },
}

export const AgentPage: NextPage<AgentPageProps> = ({
  SEO,
  agent,
  intro,
  reviews,
  soldProperties,
  latestProperties,
  form,
  sellPropertyBox,
  workWithAgentBox,
}) => {
  const { current, ref: formRef } = useRefCallback();

  const internalGoToPage = React.useCallback((urlObject: URLObject) => () => {
    goToPage(urlObject);
  }, []);

  const goToReviewPage = React.useCallback((url?: string) => () => {
    goToExternalUrl(url ?? DOVEIT_TRUSTPILOT_REVIEWS_PAGE);
  }, []);

  const onCreateLeadSuccess = React.useCallback((lead: Lead) => {
    sendLeadSubmitEventAndRedirect('main', lead);
  }, []);

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(
          getAgentStructuredData(agent, SEO.canonical),
        )}
      />
      <PageLayout>
        <PageLayout.Header>
          <PageSection>
            <PageSection.Wrapper data-ref="agent-information">
              <Grid gutter={SpacingName.SMALL}>
                <Grid.Unit size={{ LG: 1 / 4 }}>
                  <div css={styles.agentPhoto}>
                    {agent.photo ? (
                      <Image
                        src={agent.photo.url}
                        width={agent.photo.width}
                        height={agent.photo.height}
                        alt={`${agent.name} ${agent.surname}`}
                        title={`${agent.name} ${agent.surname}`}
                      />
                    ) : (
                      <Image
                        src={requireFromStaticAsset('agents/agent-placeholder.svg')}
                        alt={`Agente ${agent.name} ${agent.surname} Dove.it}`}
                      />
                    )}
                  </div>
                </Grid.Unit>
                <Grid.Unit size={{ LG: 3 / 4 }}>
                  <Typography.H2
                    tagName="h1"
                    color="brand.primary"
                  >
                    {intro.title}
                  </Typography.H2>
                  <Typography.BODY tagName="p">
                    <strong>{agent.jobTitleType.name}{agent.area ? ` - ${agent.area}` : ''}</strong>
                  </Typography.BODY>
                  <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                    <Markdown>
                      {agent.biography}
                    </Markdown>
                  </Spacing>
                  {agent.interview && (
                    <Spacing margin={[SpacingName.SMALLER, 0, 0]}>
                      <Button
                        href={agent.interview}
                        label={intro.cta.label}
                        variant={ButtonVariant.LIGHT}
                        withoutSpacing
                        iconRight
                        iconDirection={SymbolDirection.RIGHT}
                        icon={ICON_ARROW_CIRCLE}
                        color={ButtonColor.PRIMARY}
                        dataRef={intro.cta.dataRef}
                        dataTracking={intro.cta.dataTracking}
                      />
                    </Spacing>
                  )}
                  {(agent.realEstateAgencies.length === 0 && agent.agencies.length > 0) && (
                    <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                      <div
                        css={styles.agencies}
                        data-ref="agencies"
                      >
                        {agent.agencies.map((agency: domain.Agency) => {
                          const agencyUrlObject = buildAgencyByLocalityURLObject({
                            locality: agency.locality.name,
                          });

                          return (
                            <div key={agency.id}>
                              <Button
                                label={`Agenzia - ${agency.locality.name}`}
                                aria-label="agency-link"
                                href={agencyUrlObject.as}
                                variant={ButtonVariant.LIGHT}
                                withoutSpacing
                                icon={OFFICE_BUILDING_MARKER_OUTLINE}
                                color={ButtonColor.PRIMARY}
                                dataRef="agency-button"
                                onClick={() => goToPage(agencyUrlObject)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </Spacing>
                  )}

                  {agent.realEstateAgencies.length > 0 && (
                    <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                      <div data-ref="real-estate-agencies">
                        {agent.realEstateAgencies.map((agency) => {
                          const agencyUrlObject = buildRealEstateAgencyURLObject({
                            locality: agency.agencyByLocalityParent.locality.slug,
                            slug: agency.slug,
                          });

                          return (
                            <div key={agency.id}>
                              <Button
                                label={agency.name}
                                aria-label="real-estate-agency-link"
                                variant={ButtonVariant.LIGHT}
                                href={agencyUrlObject.as}
                                withoutSpacing
                                icon={OFFICE_BUILDING_MARKER_OUTLINE}
                                color={ButtonColor.PRIMARY}
                                dataRef="agency-button"
                                onClick={() => goToPage(agencyUrlObject)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </Spacing>
                  )}
                </Grid.Unit>
              </Grid>
            </PageSection.Wrapper>
          </PageSection>
        </PageLayout.Header>

        <PageLayout.Wrapper>
          <Grid gutter={SpacingName.SMALL}>
            <Grid.Unit size={{ LG: 1 / 4 }}>
              <div data-ref="sell-property-box">
                <Typography.H4 tagName="h3">
                  {sellPropertyBox.title}
                </Typography.H4>
                <Spacing margin={[SpacingName.SMALL, 0]}>
                  <Typography.BODY>
                    <Markdown>
                      {sellPropertyBox.description}
                    </Markdown>
                  </Typography.BODY>
                </Spacing>
                <Button
                  label={sellPropertyBox.cta.label}
                  dataRef={sellPropertyBox.cta.dataRef}
                  dataTracking={sellPropertyBox.cta.dataTracking}
                  onClick={scrollToRef(current)}
                  size={ButtonSize.SMALL}
                  color={ButtonColor.PRIMARY}
                />
              </div>
              <Spacing
                margin={[SpacingName.BIG, 0, 0]}
                data-ref="work-with-agent"
              >
                <Typography.H4 tagName="h3">
                  {workWithAgentBox.title}
                </Typography.H4>
                <Spacing margin={[SpacingName.SMALL, 0]}>
                  <Typography.BODY>
                    {workWithAgentBox.description}
                  </Typography.BODY>
                </Spacing>
                <Button
                  size={ButtonSize.SMALL}
                  label={workWithAgentBox.cta.label}
                  dataRef={workWithAgentBox.cta.dataRef}
                  dataTracking={workWithAgentBox.cta.dataTracking}
                  href={JOIN_OUR_AGENTS_AS}
                  onClick={internalGoToPage({
                    url: JOIN_OUR_AGENTS_URL,
                    as: JOIN_OUR_AGENTS_AS,
                  })}
                />
              </Spacing>
            </Grid.Unit>
            <Grid.Unit size={{ LG: 3 / 4 }}>
              {agent.reviews.length > 0 && (
                <PageSection
                  padding={[0, 0, SpacingName.BIGGER]}
                  data-ref="reviews"
                >
                  <PageSection.Title
                    size={TypographyName.H3}
                    content={reviews.title}
                  />
                  <Spacing margin={[SpacingName.SMALLER, 0, 0]}>
                    <Button
                      href={reviews.trustPilot.href}
                      label={reviews.trustPilot.label}
                      rel="nofollow"
                      variant={ButtonVariant.LIGHT}
                      withoutSpacing
                      iconRight
                      iconDirection={SymbolDirection.RIGHT}
                      icon={ICON_ARROW_CIRCLE}
                      color={ButtonColor.PRIMARY}
                      dataRef={reviews.trustPilot.dataRef}
                      dataTracking={reviews.trustPilot.dataTracking}
                    />
                  </Spacing>
                  <PageSection.Block>
                    <CardsHList inline={3}>
                      {agent.reviews.map((review) => (
                        <CardsHList.Item
                          key={review.id}
                        >
                          <ReviewCard
                            {...review}
                            onClick={goToReviewPage(review.url)}
                          />
                        </CardsHList.Item>
                      ))}
                    </CardsHList>
                  </PageSection.Block>
                  <PageSection.Actions textAlign={TextAlign.CENTER}>
                    <Button
                      label={reviews.cta.label}
                      onClick={scrollToRef(current)}
                      dataRef={reviews.cta.dataRef}
                      dataTracking={reviews.cta.dataTracking}
                    />
                  </PageSection.Actions>
                </PageSection>
              )}
              {soldProperties.properties.content.length > 0 && (
                <PageSection
                  padding={[0, 0, SpacingName.BIGGER]}
                  data-ref="sold-properties-section"
                >
                  <PageSection.Title
                    size={TypographyName.H3}
                    content={soldProperties.title}
                  />
                  <PageSection.Block>
                    <CardsHList inline={3}>
                      {soldProperties.properties.content.map((searchResult) => (
                        <CardsHList.Item key={searchResult.id}>
                          <SoldPropertyCard
                            title={`${searchResult.locality}${(searchResult.suburb ? ` - ${searchResult.suburb}` : '')}`}
                            saving={searchResult.savingLabel}
                            image={searchResult.coverImageSet.small}
                            banner="Venduto"
                          />
                        </CardsHList.Item>
                      ))}
                    </CardsHList>
                  </PageSection.Block>
                </PageSection>
              )}
              {latestProperties.properties.content.length > 0 && (
                <PageSection
                  padding={[0, 0, SpacingName.BIGGER]}
                  data-ref="latest-properties-section"
                >
                  <PageSection.Title
                    size={TypographyName.H3}
                    content={latestProperties.title}
                  />
                  <PageSection.Block>
                    <PropertySearchResultCardList
                      inline={3}
                      sideSpacing={0}
                      items={latestProperties.properties.content.map((searchResult) => ({
                        searchResult,
                        urlObject: buildListingURLObject(searchResult),
                        onClick: internalGoToPage(buildListingURLObject(searchResult)),
                      }))}
                    />
                  </PageSection.Block>
                  {latestProperties.properties.totalElements > 3 && (
                    <PageSection.Actions textAlign={TextAlign.CENTER}>
                      <Button
                        label={latestProperties.cta.label}
                        dataRef={latestProperties.cta.dataRef}
                        onClick={internalGoToPage(buildSerpURLObject({ province: agent.agencies[0].locality.province.slug }))}
                      />
                    </PageSection.Actions>
                  )}
                </PageSection>
              )}
              <PageSection
                padding={[0, 0, SpacingName.BIGGER]}
                data-ref="lead-form-section"
              >
                <PageSection.Wrapper>
                  <PageSection.Title
                    content={form.title}
                    textAlign={TextAlign.CENTER}
                  />
                  <div ref={formRef}>
                    <CreateLeadSection
                      title={form.label}
                      source={AGENT_PAGE_LEAD_SOURCE}
                      campaign={agent.slug}
                      dataRef="lead-form-main"
                      onSuccess={onCreateLeadSuccess}
                      dataTracking="lead-form-main-submit"
                    />
                  </div>
                </PageSection.Wrapper>
              </PageSection>
            </Grid.Unit>
          </Grid>
        </PageLayout.Wrapper>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<AgentPageProps>(async ({ query }) => {
  const graphQLClient = initGraphQLClient();

  const [data, { data: { agent } }] = await Promise.all([
    importDynamicData('pages/agent.yml'),
    graphQLClient.query<{ agent: domain.Agent & { realEstateAgencies: domain.RealEstateAgency[] } }>({
      query: AGENT_BY_SLUG,
      variables: {
        reviews: 9,
        slug: query.slug,
        imageWidth: AGENT_IMAGE_WIDTH,
        imageHeight: AGENT_IMAGE_HEIGHT,
      },
    }),
  ]);

  // finché non viene deciso dove fare redirect quando mancano i dati di località, questo deve stare prima del check not null per garantire il redirect
  if (agent && (!agent.biography || (!agent.agencies[0].locality.province && agent.agencies[0].locality.name))) {
    return {
      redirect: {
        destination: buildAgencyByLocalityURLObject({ ...query, locality: agent.agencies[0].locality.name }).as,
        permanent: true,
      },
    };
  }

  if (!agent || (!agent.agencies[0].locality.province && !agent.agencies[0].locality.name)) {
    return {
      redirect: {
        destination: buildAgenciesURLObject(query).as,
        permanent: true,
      },
    };
  }

  let latestProvinceSoldProperties: Pick<PropertiesResponse, 'content' | 'totalElements'> = { content: [], totalElements: 0 };
  let latestProvinceProperties: Pick<PropertiesResponse, 'content' | 'totalElements'> = { content: [], totalElements: 0 };

  const franchisingAgencies = agent.realEstateAgencies.filter((realEstateAgency: domain.RealEstateAgency) => realEstateAgency.franchising);
  const isAFranchisingAgent = franchisingAgencies.length > 0;
  const franchisingAgentEmails = franchisingAgencies!
    .flatMap((franchisingAgency: domain.RealEstateAgency) => (franchisingAgency.agents || [])
      .map((franchisingAgent: domain.Agent) => franchisingAgent.email))
    .reduce((acc, curr) => (acc.includes(curr) ? acc : [...acc, curr]),
      []);

  const [latestAgentSoldProperties, latestAgentProperties] = await Promise.all([
    getAll({
      status: Status.VENDUTO,
      agentEmail: isAFranchisingAgent ? franchisingAgentEmails : agent.email,
    }, {
      size: SOLD_PROPERTY_RESULT_SIZE,
      sort: {
        sold_at: SortOrder.DESC,
      },
    }).catch((err) => {
      logger.warn({ err }, 'Error while retrieving latest sold properties');
      return EMPTY_PROPERTY_RESPONSE;
    }),
    getAll({
      status: Status.LIVE,
      agentEmail: isAFranchisingAgent ? franchisingAgentEmails : agent.email,
    }, {
      size: PROPERTY_RESULT_SIZE,
      sort: {
        published_at: SortOrder.DESC,
      },
    }).catch((err) => {
      logger.warn({ err }, 'Error while retrieving latest agent properties for sale');
      return EMPTY_PROPERTY_RESPONSE;
    }),
  ]);

  if (!isAFranchisingAgent && latestAgentSoldProperties.totalElements < SOLD_PROPERTY_RESULT_SIZE) {
    latestProvinceSoldProperties = await getAll({
      status: Status.VENDUTO,
      province: agent.agencies[0].locality.province.slug,
      exclude: latestAgentSoldProperties.content.map((property) => property.id),
    }, {
      size: SOLD_PROPERTY_RESULT_SIZE,
      sort: {
        sold_at: SortOrder.DESC,
      },
    }).catch((err) => {
      logger.warn({ err }, 'Error while retrieving latest province sold properties');
      return EMPTY_PROPERTY_RESPONSE;
    });
  }
  if (!isAFranchisingAgent && latestAgentProperties.totalElements < PROPERTY_RESULT_SIZE) {
    latestProvinceProperties = await getAll({
      status: Status.LIVE,
      province: agent.agencies[0].locality.province.slug,
      exclude: latestAgentProperties.content.map((property) => property.id),
    }, {
      size: PROPERTY_RESULT_SIZE,
      sort: {
        published_at: SortOrder.DESC,
      },
    }).catch((err) => {
      logger.warn({ err }, 'Error while retrieving latest province properties for sale');
      return EMPTY_PROPERTY_RESPONSE;
    });
  }

  const {
    SEO,
    intro,
    sellPropertyBox,
    workWithAgentBox,
    reviews,
    soldProperties: soldPropertiesData,
    latestProperties: latestPropertiesData,
    form,
  } = template(data, {
    name: agent.name,
    surname: agent.surname,
    jobTitle: agent.jobTitleType.name,
    evaluationHREF: buildEvaluationURLObject().as,
  });

  const soldProperties: Pick<PropertiesResponse, 'content' | 'totalElements'> = {
    content: [...latestAgentSoldProperties.content, ...latestProvinceSoldProperties.content].slice(0, SOLD_PROPERTY_RESULT_SIZE),
    totalElements: latestAgentSoldProperties.totalElements + latestProvinceSoldProperties.totalElements,
  };

  const properties: Pick<PropertiesResponse, 'content' | 'totalElements'> = {
    content: [...latestAgentProperties.content, ...latestProvinceProperties.content].slice(0, PROPERTY_RESULT_SIZE),
    totalElements: latestAgentProperties.totalElements + latestProvinceProperties.totalElements,
  };

  return {
    props: {
      ...data,
      SEO: {
        ...SEO,
        canonical: relativeToAbsoluteURL(buildAgentURLObject({ slug: agent.slug }).as),
      },
      agent,
      intro,
      soldProperties: {
        ...soldPropertiesData,
        properties: soldProperties,
      },
      latestProperties: {
        ...latestPropertiesData,
        properties,
      },
      reviews,
      sellPropertyBox,
      workWithAgentBox,
      form,
    },
  };
});

export default withError(AgentPage);
